import * as React from 'react'
import { Tooltip } from '@mui/material'
import {
  DarkMode as DarkModeIcon, LightMode as LightModeIcon, Search as SearchIcon, RefreshRounded, AttachFileRounded,
  SupportAgentOutlined, TimerOutlined, TuneRounded,
  ChatBubbleOutlineRounded,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { useDispatch, useSelector } from '../store'
import { fetchTicketTypes, toggleViewOne } from '../store/support/actionCreator'
import { fetchAdmins } from '../store/user/actionCreator'
import { Ticket } from '../store/support/reducer'
import { EnumTicketStatus } from '../enum/statusTicket'
import { useAdminTheme } from '../hook/useAdminTheme'
import { useAuthEffect } from '../hook/useAuthEffect'
import { api } from '../function/axios'
import { markTicketSeen, useSupportAlerts } from '../hook/useSupportAlerts'
import { useLocation, useNavigate } from 'react-router-dom'
import { TicketPanelV2 } from '../component/support-v2/TicketPanelV2'
import { TicketTypesDialogV2 } from '../component/support-v2/TicketTypesDialogV2'
import {
  ago, dueAt, dueLabel, isOverdue, loadAllTickets, personName, PRIORITY_LABEL, PRIORITY_TONE, STATUS_LABEL, STATUS_TONE, typeColor,
} from '../component/support-v2/supportTickets'
import l from './SubscribersPageV2.module.css'
import s from '../component/support-v2/SupportV2.module.css'

/*
 * The support desk in the v2 admin design. The classic page (SupportPage.tsx)
 * is untouched; this one reads the same tickets (GET /ticket, all of them —
 * the queue's totals and filters need the whole list), changes them through
 * the same calls and shows one ticket beside the queue (TicketPanelV2) with
 * its conversation (ChatV2). A ticket opened from elsewhere (a subscriber's
 * Tickets tab → "View in Support", support.viewOne) opens here too.
 *
 * J / K move through the queue, Esc closes the ticket.
 */

type StatusFilter = 'active' | 'open' | 'in-progress' | 'closed' | 'all'
type Sort = 'newest' | 'oldest' | 'priority' | 'due'
const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 }

export function SupportPageV2() {
  const dispatch = useDispatch()
  const { theme, toggleTheme } = useAdminTheme()
  const me = useSelector((state) => state.settings)
  const { admins } = useSelector((state) => state.user)
  const { ticketTypes, viewOne } = useSelector((state) => state.support)

  const [tickets, setTickets] = React.useState<Ticket[] | null>(null)
  const [progress, setProgress] = React.useState<[number, number] | null>(null)
  const [query, setQuery] = React.useState('')
  const [status, setStatus] = React.useState<StatusFilter>('active')
  const [priority, setPriority] = React.useState<string>('all')
  const [typeId, setTypeId] = React.useState<number | 'all'>('all')
  const [flag, setFlag] = React.useState<'none' | 'mine' | 'unassigned' | 'overdue'>('none')
  const [sort, setSort] = React.useState<Sort>('newest')
  const [openId, setOpenId] = React.useState<number | null>(null)
  const [typesOpen, setTypesOpen] = React.useState(false)
  const [, tick] = React.useState(0)

  useAuthEffect(() => {
    dispatch(fetchAdmins)
    dispatch(fetchTicketTypes)
  })

  const load = React.useCallback(async () => {
    try {
      const list = await loadAllTickets((a, b) => setProgress([a, b]))
      setTickets(list)
    } catch {
      setTickets((t) => t || [])
    } finally {
      setProgress(null)
    }
  }, [])
  React.useEffect(() => { void load() }, [load])

  // due times count down without a reload
  React.useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 60000)
    return () => window.clearInterval(id)
  }, [])

  /*
    WHAT IS NEW ON EACH TICKET — the admin's unseen support alerts per ticket
    (GET /notifications/support-unseen): a new ticket, new customer comments.
    Read on open and with every alert; a ticket's go when it is opened, all of
    them when the menu's Support clears the counts.
  */
  const [unseen, setUnseen] = React.useState<Record<number, { ticket: boolean; comments: number }>>({})
  const loadUnseen = React.useCallback(async () => {
    try {
      const { data } = await api({ method: 'GET', url: '/notifications/support-unseen' })
      const map: Record<number, { ticket: boolean; comments: number }> = {}
      ;(Array.isArray(data?.list) ? data.list : []).forEach((x: any) => { map[Number(x.ticketId)] = { ticket: !!x.ticket, comments: Number(x.comments) || 0 } })
      setUnseen(map)
    } catch { /* the marks stay as they were */ }
  }, [])
  React.useEffect(() => { void loadUnseen() }, [loadUnseen])

  // a new ticket or comment (support alerts) — the list is read again, so it shows, with its mark
  const alertCounts = useSupportAlerts(() => { void load(); void loadUnseen() })
  React.useEffect(() => {
    if (!alertCounts.tickets && !alertCounts.comments) setUnseen({})
  }, [alertCounts.tickets, alertCounts.comments])

  // ?ticket=ID — from a support alert (the pop-up, the bell): open it once the list is there
  const location = useLocation()
  const navigate = useNavigate()
  const wanted = Number(new URLSearchParams(location.search).get('ticket')) || null
  React.useEffect(() => {
    if (!wanted || !tickets) return
    if (tickets.some((t) => t.id === wanted)) {
      setStatus('all')
      setOpenId(wanted)
    }
    navigate(location.pathname, { replace: true })
  }, [wanted, tickets, navigate, location.pathname])

  // an opened ticket's alerts are seen — off the menu's counts
  React.useEffect(() => {
    if (!openId) return
    void markTicketSeen(openId)
    setUnseen((m) => {
      if (!m[openId]) return m
      const next = { ...m }
      delete next[openId]
      return next
    })
  }, [openId])

  // a ticket handed over from elsewhere
  React.useEffect(() => {
    if (!viewOne) return
    setTickets((list) => (list && !list.some((t) => t.id === viewOne.id) ? [viewOne, ...list] : list))
    setStatus('all')
    setOpenId(viewOne.id)
  }, [viewOne])

  const all = tickets || []
  const now = Date.now()
  const activeT = all.filter((t) => t.status !== EnumTicketStatus.CLOSED)
  const kpi = {
    open: all.filter((t) => t.status === EnumTicketStatus.OPEN).length,
    progress: all.filter((t) => t.status === EnumTicketStatus.IN_PROGRESS).length,
    overdue: activeT.filter((t) => isOverdue(t, now)).length,
    unassigned: activeT.filter((t) => !t.assignee).length,
    mine: activeT.filter((t) => t.assignee?.uid === me.uid).length,
    closed: all.filter((t) => t.status === EnumTicketStatus.CLOSED).length,
  }

  const q = query.trim().toLowerCase()
  const list = all
    .filter((t) => (status === 'all' ? true : status === 'active' ? t.status !== EnumTicketStatus.CLOSED : t.status === status))
    .filter((t) => priority === 'all' || t.priority === priority)
    .filter((t) => typeId === 'all' || t.ticketType?.id === typeId)
    .filter((t) => (flag === 'mine' ? t.assignee?.uid === me.uid : flag === 'unassigned' ? !t.assignee : flag === 'overdue' ? isOverdue(t, now) : true))
    .filter((t) => !q || [String(t.id), `#${t.id}`, t.title, t.summary, personName(t.user), t.user?.email, personName(t.assignee), t.ticketType?.name]
      .some((v) => (v || '').toLowerCase().includes(q)))
    .sort((a, b) => {
      if (sort === 'oldest') return new Date(a.createdTime).getTime() - new Date(b.createdTime).getTime()
      if (sort === 'priority') return (PRIORITY_RANK[a.priority] ?? 3) - (PRIORITY_RANK[b.priority] ?? 3) || b.id - a.id
      if (sort === 'due') return (dueAt(a) ?? Infinity) - (dueAt(b) ?? Infinity) || b.id - a.id
      return new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
    })

  const open = all.find((t) => t.id === openId) || null
  const idx = open ? list.findIndex((t) => t.id === open.id) : -1
  const go = (d: number) => { const t = list[idx + d]; if (t) setOpenId(t.id) }
  const close = () => { setOpenId(null); if (viewOne) dispatch(toggleViewOne(null)) }

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return
      if (document.querySelector('.MuiDialog-root')) return
      if (e.key === 'Escape' && openId !== null) close()
      if ((e.key === 'j' || e.key === 'J') && list.length) { const t = list[idx + 1] || (idx < 0 ? list[0] : null); if (t) setOpenId(t.id) }
      if ((e.key === 'k' || e.key === 'K') && idx > 0) setOpenId(list[idx - 1].id)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const patch = (t: Ticket) => setTickets((xs) => (xs ? xs.map((x) => (x.id === t.id ? t : x)) : xs))
  const typeCounts = all.reduce<Record<number, number>>((m, t) => { if (t.ticketType) m[t.ticketType.id] = (m[t.ticketType.id] || 0) + 1; return m }, {})

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Customers</small><b>Support</b></div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search #id, title, customer, assignee" />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  const kpiBtn = (label: string, value: number, sub: string, onClick: () => void, on: boolean, tone?: string) => (
    <button type="button" className={`${l.kpi} ${s.kpiBtn} ${on ? s.kpiOn : ''}`} onClick={onClick}>
      <small>{label}</small><b style={tone && value ? { color: tone } : undefined}>{tickets ? value : '—'}</b><span>{sub}</span>
    </button>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          {kpiBtn('Open', kpi.open, 'waiting for a first answer', () => { setStatus('open'); setFlag('none') }, status === 'open' && flag === 'none', 'var(--danger)')}
          {kpiBtn('In progress', kpi.progress, 'being worked on', () => { setStatus('in-progress'); setFlag('none') }, status === 'in-progress' && flag === 'none')}
          {kpiBtn('Overdue', kpi.overdue, 'past their due time', () => { setStatus('active'); setFlag('overdue') }, flag === 'overdue', 'var(--danger)')}
          {kpiBtn('Unassigned', kpi.unassigned, 'open, nobody on it', () => { setStatus('active'); setFlag('unassigned') }, flag === 'unassigned', 'var(--warn)')}
          {kpiBtn('Mine', kpi.mine, 'open, assigned to you', () => { setStatus('active'); setFlag('mine') }, flag === 'mine')}
          {kpiBtn('Closed', kpi.closed, `of ${all.length} tickets`, () => { setStatus('closed'); setFlag('none') }, status === 'closed' && flag === 'none')}
        </div>

        <div className={l.sec}>
          <div className={s.seg}>
            {([['active', 'Active'], ['open', 'Open'], ['in-progress', 'In progress'], ['closed', 'Closed'], ['all', 'All']] as const).map(([k, label]) => (
              <button key={k} type="button" className={status === k ? s.segOn : ''} onClick={() => setStatus(k)}>{label}</button>
            ))}
          </div>
          <div className={s.chips}>
            {(['high', 'medium', 'low'] as const).map((p) => (
              <button key={p} type="button" className={`${l.chip} ${priority === p ? l.chipOn : ''}`} onClick={() => setPriority(priority === p ? 'all' : p)}>
                <i className={s.dot} style={{ background: PRIORITY_TONE[p] }} />{PRIORITY_LABEL[p]}
              </button>
            ))}
            {([['mine', 'Mine'], ['unassigned', 'Unassigned'], ['overdue', 'Overdue']] as const).map(([k, label]) => (
              <button key={k} type="button" className={`${l.chip} ${flag === k ? l.chipOn : ''}`} onClick={() => setFlag(flag === k ? 'none' : k)}>{label}</button>
            ))}
          </div>
          <div className={l.spacer} />
          <select className={s.selectSm} value={typeId} onChange={(e) => setTypeId(e.target.value === 'all' ? 'all' : Number(e.target.value))} aria-label="Type">
            <option value="all">All types</option>
            {(ticketTypes || []).map((x) => <option key={x.id} value={x.id}>{x.name} ({typeCounts[x.id] || 0})</option>)}
          </select>
          <Tooltip title="Ticket types"><button type="button" className={s.iconGhost} onClick={() => setTypesOpen(true)} aria-label="Ticket types"><TuneRounded sx={{ fontSize: 17 }} /></button></Tooltip>
          <select className={s.selectSm} value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="priority">Priority</option>
            <option value="due">Due soonest</option>
          </select>
          <Tooltip title="Reload"><button type="button" className={s.iconGhost} onClick={() => { void load() }} aria-label="Reload"><RefreshRounded sx={{ fontSize: 17 }} /></button></Tooltip>
        </div>

        <div className={`${s.desk} ${open ? s.deskSplit : ''}`}>
          <div className={`${l.card} ${s.queue}`}>
            <div className={s.queueHead}>
              <span>{tickets ? `${list.length} ${list.length === 1 ? 'ticket' : 'tickets'}` : progress ? `Loading ${progress[0]} of ${progress[1]}…` : 'Loading…'}</span>
            </div>
            <div className={s.queueList}>
              {tickets && !list.length && <div className={l.empty}>{q || flag !== 'none' || priority !== 'all' || typeId !== 'all' ? 'Nothing matches the filter' : 'No tickets here'}</div>}
              {list.map((t) => {
                const due = dueLabel(t, now)
                const news = unseen[t.id]
                /*
                  Marked even without support notifications: an open ticket whose
                  last word is the customer's waits for an answer — one comment
                  (their own question) is a new ticket, more is a new reply. It
                  stays until support answers; the alert counts, when there are
                  any, say how many are new.
                */
                const lc = t.lastComment
                const waiting = !!lc?.fromCustomer && t.status !== EnumTicketStatus.CLOSED
                const isNewTicket = !!news?.ticket || (waiting && (lc?.count || 0) <= 1)
                const newReplies = news?.comments || (waiting && (lc?.count || 0) > 1 ? -1 : 0)
                return (
                  <button key={t.id} type="button" className={`${s.item} ${t.id === openId ? s.itemOn : ''} ${isNewTicket || newReplies ? s.itemNew : ''}`} onClick={() => setOpenId(t.id)}>
                    <i className={s.stripe} style={{ background: PRIORITY_TONE[t.priority] || 'var(--border)' }} title={`${PRIORITY_LABEL[t.priority] || ''} priority`} />
                    <div className={s.itemMain}>
                      <div className={s.itemTop}>
                        <span className={s.idTag}>#{t.id}</span>
                        <b>{t.title}</b>
                        {!!t.attachments?.length && <AttachFileRounded sx={{ fontSize: 14 }} className={s.muted} />}
                        {/* unseen by this admin: a new ticket, new comments from the customer */}
                        {isNewTicket && <span className={s.newTicket} title="A new ticket — not answered yet">New</span>}
                        {newReplies > 0 && (
                          <span className={s.newComments} title={`${newReplies} new comment${newReplies === 1 ? '' : 's'}`}>
                            <ChatBubbleOutlineRounded sx={{ fontSize: 12 }} />{newReplies > 9 ? '9+' : newReplies} new
                          </span>
                        )}
                        {newReplies < 0 && (
                          <span className={s.newComments} title="The customer wrote last — waiting for an answer">
                            <ChatBubbleOutlineRounded sx={{ fontSize: 12 }} />New reply
                          </span>
                        )}
                      </div>
                      <div className={s.itemSub}>
                        <span>{personName(t.user) || 'Customer'}</span>
                        {t.ticketType && <span className={s.type}><i style={{ background: typeColor(t.ticketType.color) }} />{t.ticketType.name}</span>}
                        <span className={s.summaryLine}>{t.summary}</span>
                      </div>
                    </div>
                    <div className={s.itemSide}>
                      <span className={s.status} style={{ color: STATUS_TONE[t.status], background: `color-mix(in srgb, ${STATUS_TONE[t.status]} 12%, transparent)` }}>{STATUS_LABEL[t.status] || t.status}</span>
                      {due
                        ? <span className={`${s.due} ${due.late ? s.late : ''}`}><TimerOutlined sx={{ fontSize: 13 }} />{due.text}</span>
                        : <span className={s.muted}>{ago(t.createdTime, now)}</span>}
                      <Tooltip title={t.assignee ? `Assignee: ${personName(t.assignee)}` : 'Unassigned'}>
                        <span className={`${s.assignee} ${t.assignee ? '' : s.assigneeNone}`}>
                          {t.assignee?.avatar ? <img src={t.assignee.avatar} alt="" /> : t.assignee ? (personName(t.assignee)[0] || '?').toUpperCase() : <SupportAgentOutlined sx={{ fontSize: 14 }} />}
                        </span>
                      </Tooltip>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {open && (
            <TicketPanelV2
              key={open.id}
              ticket={open}
              admins={admins || []}
              types={ticketTypes || []}
              meUid={me.uid}
              onPatch={patch}
              onDeleted={(id) => { setTickets((xs) => (xs ? xs.filter((x) => x.id !== id) : xs)); close() }}
              onClose={close}
              onPrev={idx > 0 ? () => go(-1) : undefined}
              onNext={idx >= 0 && idx < list.length - 1 ? () => go(1) : undefined}
              onManageTypes={() => setTypesOpen(true)}
            />
          )}
        </div>
      </div>

      {typesOpen && <TicketTypesDialogV2 types={ticketTypes || []} counts={typeCounts} theme={theme} onClose={() => setTypesOpen(false)} />}
    </PrivatePage>
  )
}
